import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  Activity, 
  RefreshCw,
  UserCheck, 
  UserX, 
  Clock, 
  BarChart3, 
  CreditCard, 
  MessageSquare,
  Home,
  FileText,
  Edit3,
  Plus,
  Target,
  LogOut,
  X,
  Trash2,
  Filter,
  Menu,
  CheckCircle,
  XCircle,
  Info,
  AlertCircle,
  MessageCircle,
  PieChart,
  Settings,
  Download,
  Save
} from 'lucide-react'
import { useAdminData } from '../contexts/AdminDataContext'
import Messages from './Messages'
import { useAdminAuth } from '../contexts/AdminAuthContext'
import { supabaseAdmin } from '../lib/supabaseClient'


const Dashboard = () => {
  const { stats, loading, refreshData, loadPlanos, planos, usuarios, loadUsuarios, clearCache } = useAdminData()
  const { signOut } = useAdminAuth()
  
  // Estados para o menu e navegação
  const [activeSection, setActiveSection] = useState('dashboard')
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Estados para gestão de planos
  const [showPlanForm, setShowPlanForm] = useState(false)
  const [editingPlan, setEditingPlan] = useState<any>(null)
  const [planForm, setPlanForm] = useState({
    nome: '',
    descricao: '',
    valor: '',
    duracao_dias: '',
    recursos: '',
    ativo: true
  })
  const [loadingPlans, setLoadingPlans] = useState(false)

  // Estados para gestão de usuários
  const [showUserForm, setShowUserForm] = useState(false)
  const [editingUser, setEditingUser] = useState<any>(null)
  const [userForm, setUserForm] = useState({
    nome: '',
    email: '',
    empresa_nome: '',
    whatsapp: '',
    ativo: true,
    is_teste: false,
    admin: false
  })
  const [loadingUsers, setLoadingUsers] = useState(false)

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Menu items específicos do admin - EXATAMENTE IGUAL ao AdminLayout.tsx
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'users', label: 'Usuários', icon: Users },
    { id: 'plans', label: 'Planos', icon: CreditCard },
    { id: 'tickets', label: 'Chamados', icon: MessageSquare },
    { id: 'messages', label: 'Mensagens', icon: MessageSquare },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Configurações', icon: Settings },
  ]

  // Função para navegar e atualizar seção ativa
  const handleNavigation = (item: any) => {
    setActiveSection(item.id)
    // Fechar sidebar no mobile após clicar em item
    if (window.innerWidth < 1024) {
      setSidebarOpen(false)
    }
  }

  // Função para renderizar o conteúdo baseado na seção ativa
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="space-y-6 w-full max-w-none">
            {/* Header */}
            <div className="flex items-center justify-between w-full">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-3">Dashboard Administrativo</h1>
                <p className="text-lg text-gray-600">
                  Visão geral do sistema Aguiar Gestor
                </p>
              </div>

            </div>

            {/* Estatísticas de Usuários */}
            <div className="w-full">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Estatísticas de Usuários</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6 w-full">
                {statsCards.map((card, index) => (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="stats-card"
                  >
                    <div className="flex items-center">
                      <div className={`stats-card-icon ${card.color}`}>
                        <card.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="stats-card-content">
                        <p className="stats-card-title">{card.title}</p>
                        <p className="stats-card-value">{card.value}</p>
                        <div className="flex items-center">
                          <span className={`stats-card-change ${
                            card.changeType === 'positive' ? 'text-green-600' :
                            card.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {card.change}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="stats-card-description">{card.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Estatísticas de Faturas */}
            <div className="mt-6 w-full">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Estatísticas de Faturas</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6 w-full">
                {financialCards.map((card, index) => (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (index + 4) * 0.1 }}
                    className="stats-card"
                  >
                    <div className="flex items-center">
                      <div className={`stats-card-icon ${card.color}`}>
                        <card.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="stats-card-content">
                        <p className="stats-card-title">{card.title}</p>
                        <p className="stats-card-value">{card.value}</p>
                        <div className="flex items-center">
                          <span className={`stats-card-change ${
                            card.changeType === 'positive' ? 'text-green-600' :
                            card.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {card.change}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="stats-card-description">{card.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Estatísticas de Negócio e Faturas */}
            <div className="mt-6 w-full">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Estatísticas de Negócio e Faturas</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6 w-full">
                {businessCards.map((card, index) => (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (index + 8) * 0.1 }}
                    className="stats-card"
                  >
                    <div className="flex items-center">
                      <div className={`stats-card-icon ${card.color}`}>
                        <card.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="stats-card-content">
                        <p className="stats-card-title">{card.title}</p>
                        <p className="stats-card-value">{card.value}</p>
                        <div className="flex items-center">
                          <span className={`stats-card-change ${
                            card.changeType === 'positive' ? 'text-green-600' :
                            card.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {card.change}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="stats-card-description">{card.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Resumo do Sistema */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
                             className="admin-card mt-6"
            >
              <h3 className="text-lg font-medium text-gray-900 mb-6">Resumo do Sistema</h3>
              <p className="text-gray-600 mb-8">
                O sistema Aguiar Gestor está funcionando normalmente com dados em tempo real.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6 w-full">
                                 <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200 hover:bg-green-100 transition-colors">
                  <div className="text-sm font-medium text-green-800 mb-2">Status</div>
                  <div className="text-xl font-semibold text-green-600">Sistema Operacional</div>
                </div>
                                 <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200 hover:bg-blue-100 transition-colors">
                  <div className="text-sm font-medium text-blue-800 mb-2">Última Atualização</div>
                  <div className="text-xl font-semibold text-blue-600">{formatDate(new Date().toISOString())}</div>
                </div>
                                 <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-200 hover:bg-purple-100 transition-colors">
                  <div className="text-sm font-medium text-purple-800 mb-2">Usuários Online</div>
                  <div className="text-xl font-semibold text-purple-600">{stats?.usuariosAtivos || 0} ativos</div>
                </div>
              </div>
            </motion.div>
          </div>
        )
      
      case 'users':
        return (
          <div className="space-y-6">
                         <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
               <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Gestão de Usuários</h2>
               <div className="flex space-x-3">
                                   <button 
                    onClick={async () => {
                      console.log('🔄 [Dashboard] Refresh manual de usuários...')
                      
                      // Teste direto no Supabase
                      console.log('🧪 [Dashboard] Testando query direta...')
                      const { data, error } = await supabaseAdmin
                        .from('usuario')
                        .select('*')
                        .limit(1)
                      
                      console.log('🧪 [Dashboard] Query direta resultado:', { data, error })
                      
                      await loadUsuarios()
                    }}
                    className="font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Atualizar</span>
                  </button>
                 <button 
                   onClick={() => {
                     setEditingUser(null)
                     setUserForm({
                       nome: '',
                       email: '',
                       empresa_nome: '',
                       whatsapp: '',
                       ativo: true,
                       is_teste: false,
                       admin: false
                     })
                     setShowUserForm(true)
                   }}
                   className="w-full sm:w-auto font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center sm:justify-start space-x-2 bg-admin-600 hover:bg-admin-700 text-white"
                 >
                   <Plus className="w-4 h-4" />
                   <span>Novo Usuário</span>
                 </button>
               </div>
             </div>
            
            {/* Lista de Usuários */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Cadastro</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usuarios.map((usuario: any) => (
                      <tr key={usuario.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {usuario.nome || 'Sem nome'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{usuario.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {usuario.empresa_nome || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            usuario.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {usuario.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            usuario.is_teste ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {usuario.is_teste ? 'Teste' : 'Pago'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(usuario.created_at)}
                        </td>
                                                 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                           <div className="flex space-x-2">
                             <button
                               onClick={() => {
                                 setEditingUser(usuario)
                                 setUserForm({
                                   nome: usuario.nome || '',
                                   email: usuario.email || '',
                                   empresa_nome: usuario.empresa_nome || '',
                                   whatsapp: usuario.whatsapp || '',
                                   ativo: usuario.ativo || false,
                                   is_teste: usuario.is_teste || false,
                                   admin: usuario.admin || false
                                 })
                                 setShowUserForm(true)
                               }}
                               className="text-admin-600 hover:text-admin-900"
                               title="Editar usuário"
                             >
                               <Edit3 className="w-4 h-4" />
                             </button>
                             <button
                               onClick={() => deleteUser(usuario.id)}
                               className="text-red-600 hover:text-red-900"
                               title="Excluir usuário"
                             >
                               <Trash2 className="w-4 h-4" />
                             </button>
                           </div>
                         </td>
                      </tr>
                    ))}
                    {usuarios.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                          Nenhum usuário encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )

      case 'plans':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Gestão de Planos</h2>
              <button 
                onClick={() => {
                  setEditingPlan(null)
                  setPlanForm({
                    nome: '',
                    descricao: '',
                    valor: '',
                    duracao_dias: '',
                    recursos: '',
                    ativo: true
                  })
                  setShowPlanForm(true)
                }}
                className="w-full sm:w-auto font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center sm:justify-start space-x-2 bg-admin-600 hover:bg-admin-700 text-white"
              >
                <Plus className="w-4 h-4" />
                <span>Novo Plano</span>
              </button>
            </div>
            
            {/* Lista de Planos */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recursos</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duração</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {planos.map((plan: any) => (
                      <tr key={plan.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{plan.nome}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="truncate max-w-xs" title={plan.descricao}>
                            {plan.descricao}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="truncate max-w-xs" title={Array.isArray(plan.recursos) ? plan.recursos.join(', ') : plan.recursos}>
                            {Array.isArray(plan.recursos) ? plan.recursos.join(', ') : plan.recursos || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">R$ {plan.valor}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{plan.duracao_dias} dias</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            plan.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {plan.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingPlan(plan)
                                                                 setPlanForm({
                                   nome: plan.nome,
                                   descricao: plan.descricao,
                                   valor: plan.valor,
                                   duracao_dias: plan.duracao_dias,
                                   recursos: Array.isArray(plan.recursos) ? plan.recursos.join(', ') : plan.recursos || '',
                                   ativo: plan.ativo
                                 })
                                setShowPlanForm(true)
                              }}
                              className="text-admin-600 hover:text-admin-900"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deletePlan(plan.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                                                              {planos.length === 0 && (
                       <tr>
                         <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                           Nenhum plano encontrado. Crie seu primeiro plano para começar.
                         </td>
                       </tr>
                     )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )

      case 'tickets':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Sistema de Chamados</h2>
              <button className="w-full sm:w-auto font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center sm:justify-start space-x-2 bg-admin-600 hover:bg-admin-700 text-white">
                <Plus className="w-4 h-4" />
                <span>Novo Chamado</span>
              </button>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <p className="text-gray-600 text-center">Seção de chamados em desenvolvimento...</p>
            </div>
          </div>
        )

      case 'messages':
        // Reaproveita a página de mensagens, renderizando dentro do Dashboard
        return (
          <div className="space-y-6">
            <Messages />
          </div>
        )

      case 'analytics':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Analytics e Relatórios</h2>
              <button className="w-full sm:w-auto font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center sm:justify-start space-x-2 bg-admin-600 hover:bg-admin-700 text-white">
                <Download className="w-4 h-4" />
                <span>Exportar Dados</span>
              </button>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <p className="text-gray-600 text-center">Seção de analytics em desenvolvimento...</p>
            </div>
          </div>
        )

      case 'settings':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Configurações do Sistema</h2>
              <button className="w-full sm:w-auto font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center sm:justify-start space-x-2 bg-admin-600 hover:bg-admin-700 text-white">
                <Save className="w-4 h-4" />
                <span>Salvar Configurações</span>
              </button>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <p className="text-gray-600 text-center">Seção de configurações em desenvolvimento...</p>
            </div>
          </div>
        )

      default:
        return (
          <div className="space-y-6">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Seção não encontrada</h2>
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <p className="text-gray-600 text-center">Esta seção ainda não foi implementada.</p>
            </div>
          </div>
        )
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-2">
              Carregando dados do sistema...
            </p>
          </div>
          <div className="w-8 h-8 border-4 border-admin-200 border-t-admin-600 rounded-full animate-spin"></div>
        </div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Erro ao carregar dados do sistema
          </p>
        </div>
      </div>
    )
  }

  const statsCards = [
    {
      title: 'Total de Usuários',
      value: stats.totalUsuarios.toString(),
      change: `${stats.usuariosAtivos} ativos`,
      changeType: 'positive' as const,
      icon: Users,
      color: 'bg-blue-500',
      description: 'Usuários cadastrados no sistema'
    },
    {
      title: 'Usuários Ativos',
      value: stats.usuariosAtivos.toString(),
      change: `${((stats.usuariosAtivos / stats.totalUsuarios) * 100).toFixed(1)}%`,
      changeType: 'positive' as const,
      icon: UserCheck,
      color: 'bg-green-500',
      description: 'Contas ativas e funcionando'
    },
    {
      title: 'Usuários em Teste',
      value: stats.usuariosTeste.toString(),
      change: `${((stats.usuariosTeste / stats.totalUsuarios) * 100).toFixed(1)}%`,
      changeType: 'neutral' as const,
      icon: Clock,
      color: 'bg-yellow-500',
      description: 'Usuários no período de teste'
    },
    {
      title: 'Usuários Inativos',
      value: stats.usuariosInativos.toString(),
      change: `${((stats.usuariosInativos / stats.totalUsuarios) * 100).toFixed(1)}%`,
      changeType: 'negative' as const,
      icon: UserX,
      color: 'bg-red-500',
      description: 'Contas desativadas'
    }
  ]

  const financialCards = [
    {
      title: 'Receita de Faturas',
      value: formatCurrency(stats.receitaFaturas),
      change: `+${formatCurrency(stats.receitaFaturasMes)} este mês`,
      changeType: 'positive' as const,
      icon: TrendingUp,
      color: 'bg-green-500',
      description: 'Total recebido via faturas'
    },
    {
      title: 'Total de Faturas',
      value: stats.totalFaturas.toString(),
      change: `${stats.faturasPagas} pagas`,
      changeType: 'positive' as const,
      icon: FileText,
      color: 'bg-blue-500',
      description: 'Faturas emitidas no sistema'
    },
    {
      title: 'Faturas Pendentes',
      value: stats.faturasPendentes.toString(),
      change: `${stats.faturasVencidas} vencidas`,
      changeType: stats.faturasVencidas > 0 ? 'negative' as const : 'neutral' as const,
      icon: Clock,
      color: stats.faturasVencidas > 0 ? 'bg-orange-500' : 'bg-yellow-500',
      description: 'Faturas aguardando pagamento'
    },
    {
      title: 'Taxa de Conversão',
      value: stats.totalFaturas > 0 ? `${((stats.faturasPagas / stats.totalFaturas) * 100).toFixed(1)}%` : '0%',
      change: `${stats.faturasPagas}/${stats.totalFaturas} pagas`,
      changeType: stats.totalFaturas > 0 && (stats.faturasPagas / stats.totalFaturas) >= 0.7 ? 'positive' as const : 'neutral' as const,
      icon: BarChart3,
      color: stats.totalFaturas > 0 && (stats.faturasPagas / stats.totalFaturas) >= 0.7 ? 'bg-emerald-500' : 'bg-purple-500',
      description: 'Percentual de faturas pagas'
    }
  ]

  const businessCards = [
    {
      title: 'Total de Planos',
      value: stats.totalPlanos.toString(),
      change: `${stats.planosAtivos} ativos`,
      changeType: 'positive' as const,
      icon: CreditCard,
      color: 'bg-indigo-500',
      description: 'Planos disponíveis no sistema'
    },
    {
      title: 'Faturas Vencidas',
      value: stats.faturasVencidas.toString(),
      change: `${stats.faturasPendentes} pendentes`,
      changeType: stats.faturasVencidas > 0 ? 'negative' as const : 'positive' as const,
      icon: AlertCircle,
      color: stats.faturasVencidas > 0 ? 'bg-red-500' : 'bg-green-500',
      description: 'Faturas com vencimento expirado'
    },
    {
      title: 'Chamados Abertos',
      value: stats.chamadosAbertos.toString(),
      change: `${stats.totalChamados} total`,
      changeType: 'positive' as const,
      icon: MessageSquare,
      color: 'bg-orange-500',
      description: 'Chamados de suporte pendentes'
    },
    {
      title: 'Receita Mensal',
      value: formatCurrency(stats.receitaFaturasMes),
      change: `vs ${formatCurrency(stats.receitaFaturas)} total`,
      changeType: 'positive' as const,
      icon: DollarSign,
      color: 'bg-emerald-500',
      description: 'Receita das faturas este mês'
    }
  ]

  // Função para fazer logout
  const handleLogout = async () => {
    await signOut()
    // Redirecionamento será tratado pelo contexto de autenticação
  }

  // Funções para gestão de planos
  const loadPlans = async () => {
    setLoadingPlans(true)
    try {
      // Usar o contexto para carregar planos reais do Supabase
      await loadPlanos()
      // Os planos serão atualizados automaticamente pelo contexto
    } catch (error) {
      console.error('Erro ao carregar planos:', error)
    } finally {
      setLoadingPlans(false)
    }
  }

  // Funções para gestão de usuários
  const saveUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoadingUsers(true)
    
    console.log('🔄 [Dashboard] Iniciando saveUser...')
    console.log('📝 [Dashboard] Dados do formulário:', userForm)
    console.log('✏️ [Dashboard] Editando usuário:', editingUser ? 'SIM' : 'NÃO')
    
    try {
      if (editingUser) {
        console.log('🔄 [Dashboard] Atualizando usuário existente:', editingUser.id)
        
        // Atualizar usuário existente no Supabase
        const { data, error } = await supabaseAdmin
          .from('usuario')
          .update({
            nome: userForm.nome,
            email: userForm.email,
            empresa_nome: userForm.empresa_nome,
            whatsapp: userForm.whatsapp,
            ativo: userForm.ativo,
            is_teste: userForm.is_teste,
            admin: userForm.admin
          })
          .eq('id', editingUser.id)
          .select() // Retornar dados atualizados

        console.log('📊 [Dashboard] Resposta do Supabase:', { data, error })

        if (error) {
          console.error('❌ [Dashboard] Erro ao atualizar usuário:', error)
          alert('Erro ao atualizar usuário. Tente novamente.')
          return
        }

        console.log('✅ [Dashboard] Usuário atualizado com sucesso!')
        console.log('📊 [Dashboard] Dados retornados:', data)
      } else {
        console.log('🆕 [Dashboard] Criando novo usuário...')
        
        // Criar novo usuário no Supabase
        const { data, error } = await supabaseAdmin
          .from('usuario')
          .insert({
            nome: userForm.nome,
            email: userForm.email,
            empresa_nome: userForm.empresa_nome,
            whatsapp: userForm.whatsapp,
            ativo: userForm.ativo,
            is_teste: userForm.is_teste,
            admin: userForm.admin
          })
          .select() // Retornar dados criados

        console.log('📊 [Dashboard] Resposta do Supabase:', { data, error })

        if (error) {
          console.error('❌ [Dashboard] Erro ao criar usuário:', error)
          alert('Erro ao criar usuário. Tente novamente.')
          return
        }

        console.log('✅ [Dashboard] Usuário criado com sucesso!')
        console.log('📊 [Dashboard] Dados retornados:', data)
      }
      
      // Recarregar usuários do contexto (FORCE RELOAD)
      console.log('🔄 [Dashboard] Recarregando usuários após atualização...')
      await loadUsuarios(true) // true = force reload, ignora cache
      console.log('✅ [Dashboard] Usuários recarregados com sucesso!')
      
      // Limpar formulário e fechar modal
      setShowUserForm(false)
      setEditingUser(null)
      setUserForm({
        nome: '',
        email: '',
        empresa_nome: '',
        whatsapp: '',
        ativo: true,
        is_teste: false,
        admin: false
      })
      
      // Mostrar mensagem de sucesso
      alert(editingUser ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!')
      
    } catch (error) {
      console.error('Erro ao salvar usuário:', error)
      alert('Erro inesperado. Tente novamente.')
    } finally {
      setLoadingUsers(false)
    }
  }

  const deleteUser = async (userId: string) => {
    if (!confirm('Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.')) return
    
    setLoadingUsers(true)
    
    try {
      // Excluir usuário do Supabase
      const { error } = await supabaseAdmin
        .from('usuario')
        .delete()
        .eq('id', userId)

      if (error) {
        console.error('Erro ao excluir usuário:', error)
        alert('Erro ao excluir usuário. Tente novamente.')
        return
      }

      console.log('✅ Usuário excluído com sucesso!')
      
      // Recarregar usuários do contexto
      await loadUsuarios()
      
      // Mostrar mensagem de sucesso
      alert('Usuário excluído com sucesso!')
      
    } catch (error) {
      console.error('Erro ao excluir usuário:', error)
      alert('Erro inesperado. Tente novamente.')
    } finally {
      setLoadingUsers(false)
    }
  }

  const savePlan = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoadingPlans(true)
    
    try {
      if (editingPlan) {
        // Atualizar plano existente no Supabase
        const { error } = await supabaseAdmin
          .from('plano')
          .update({
            nome: planForm.nome,
            descricao: planForm.descricao,
            valor: parseFloat(planForm.valor),
            duracao_dias: parseInt(planForm.duracao_dias),
            recursos: planForm.recursos ? planForm.recursos.split(',').map(r => r.trim()) : [],
            ativo: planForm.ativo,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingPlan.id)

        if (error) {
          console.error('Erro ao atualizar plano:', error)
          alert('Erro ao atualizar plano. Tente novamente.')
          return
        }

        console.log('✅ Plano atualizado com sucesso!')
      } else {
        // Criar novo plano no Supabase
        const { error } = await supabaseAdmin
          .from('plano')
          .insert({
            nome: planForm.nome,
            descricao: planForm.descricao,
            valor: parseFloat(planForm.valor),
            duracao_dias: parseInt(planForm.duracao_dias),
            recursos: planForm.recursos ? planForm.recursos.split(',').map(r => r.trim()) : [],
            ativo: planForm.ativo
          })

        if (error) {
          console.error('Erro ao criar plano:', error)
          alert('Erro ao criar plano. Tente novamente.')
          return
        }

        console.log('✅ Plano criado com sucesso!')
      }
      
      // Recarregar planos do contexto
      await loadPlanos()
      
      // Limpar formulário e fechar modal
      setShowPlanForm(false)
      setEditingPlan(null)
      setPlanForm({
        nome: '',
        descricao: '',
        valor: '',
        duracao_dias: '',
        recursos: '',
        ativo: true
      })
      
      // Mostrar mensagem de sucesso
      alert(editingPlan ? 'Plano atualizado com sucesso!' : 'Plano criado com sucesso!')
      
    } catch (error) {
      console.error('Erro ao salvar plano:', error)
      alert('Erro inesperado. Tente novamente.')
    } finally {
      setLoadingPlans(false)
    }
  }

  const deletePlan = async (planId: string) => {
    if (!confirm('Tem certeza que deseja excluir este plano?')) return
    
    setLoadingPlans(true)
    
    try {
      // Excluir plano do Supabase
      const { error } = await supabaseAdmin
        .from('plano')
        .delete()
        .eq('id', planId)

      if (error) {
        console.error('Erro ao excluir plano:', error)
        alert('Erro ao excluir plano. Tente novamente.')
        return
      }

      console.log('✅ Plano excluído com sucesso!')
      
      // Recarregar planos do contexto
      await loadPlanos()
      
      // Mostrar mensagem de sucesso
      alert('Plano excluído com sucesso!')
      
    } catch (error) {
      console.error('Erro ao excluir plano:', error)
      alert('Erro inesperado. Tente novamente.')
    } finally {
      setLoadingPlans(false)
    }
  }



  return (
    <div className="min-h-screen bg-gray-50 flex w-full">
      {/* Overlay para mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar - EXATAMENTE IGUAL ao Dashboard.tsx principal */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg border-r border-gray-200 flex flex-col transition-transform duration-300 ease-in-out sidebar-desktop-fixed`}>
        <div className="flex-1 flex flex-col min-h-0">
          <div className="p-6 flex-shrink-0">
            {/* Header do Sidebar com Botão de Fechar (Mobile) */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-16 h-16 bg-admin-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-xl font-bold">AG</span>
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Admin Panel</h1>
                  <p className="text-sm text-gray-600">Aguiar Gestor</p>
                </div>
              </div>
              {/* Botão de fechar para mobile */}
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Admin Info */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-admin-100 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-admin-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">Administrador</p>
                  <p className="text-xs text-gray-500 truncate">Painel de Controle</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Menu - Scrollable - EXATAMENTE IGUAL */}
          <nav className="flex-1 px-6 pb-6 overflow-y-auto sidebar-scroll">
            <div className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeSection === item.id
                        ? 'bg-admin-50 text-admin-700 border border-admin-200'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${activeSection === item.id ? 'text-admin-600' : 'text-gray-500'}`} />
                    <span className="font-medium">{item.label}</span>
                  </button>
                )
              })}
            </div>
          </nav>

          {/* Logout Button - Fixed at bottom - EXATAMENTE IGUAL */}
          <div className="p-6 flex-shrink-0 border-t border-gray-200 space-y-3">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5 text-gray-500" />
              <span className="font-medium">Sair</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0 max-w-none">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-admin-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg font-bold">AG</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Admin Panel</h1>
                <p className="text-sm text-gray-600">Aguiar Gestor</p>
              </div>
            </div>
            <div className="w-10"></div> {/* Spacer para centralizar o título */}
          </div>
        </div>

        <div className="p-4 lg:p-6">
          {renderContent()}
        </div>
      </div>

      {/* Modal de Formulário de Plano */}
      {showPlanForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingPlan ? 'Editar Plano' : 'Novo Plano'}
              </h3>
              <button
                onClick={() => setShowPlanForm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={savePlan} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Plano *</label>
                <input
                  type="text"
                  value={planForm.nome}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, nome: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                  placeholder="Ex: Plano Básico, Plano Profissional"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição *</label>
                <textarea
                  value={planForm.descricao}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, descricao: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200 resize-none"
                  placeholder="Descreva o que está incluído neste plano..."
                  required
                />
              </div>
              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Valor (R$) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={planForm.valor}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, valor: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                      placeholder="Ex: 29.90"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duração (dias) *</label>
                    <input
                      type="number"
                      min="1"
                      value={planForm.duracao_dias}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, duracao_dias: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                      placeholder="Ex: 30"
                      required
                    />
                  </div>
                </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Recursos</label>
                <textarea
                  value={planForm.recursos}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, recursos: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200 resize-none"
                  placeholder="Digite os recursos separados por vírgula (Ex: Relatórios, Exportação, Suporte)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={planForm.ativo ? 'ativo' : 'inativo'}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, ativo: e.target.value === 'ativo' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPlanForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-admin-600 text-white rounded-lg hover:bg-admin-700 transition-colors"
                >
                  {editingPlan ? 'Atualizar' : 'Criar'} Plano
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Modal de Formulário de Usuário */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </h3>
              <button
                onClick={() => setShowUserForm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={saveUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome *</label>
                  <input
                    type="text"
                    value={userForm.nome}
                    onChange={(e) => setUserForm(prev => ({ ...prev, nome: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                    placeholder="Nome completo do usuário"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
                  <input
                    type="text"
                    value={userForm.empresa_nome}
                    onChange={(e) => setUserForm(prev => ({ ...prev, empresa_nome: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                    placeholder="Nome da empresa"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp</label>
                  <input
                    type="text"
                    value={userForm.whatsapp}
                    onChange={(e) => setUserForm(prev => ({ ...prev, whatsapp: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={userForm.ativo ? 'ativo' : 'inativo'}
                    onChange={(e) => setUserForm(prev => ({ ...prev, ativo: e.target.value === 'ativo' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                  >
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Conta</label>
                  <select
                    value={userForm.is_teste ? 'teste' : 'pago'}
                    onChange={(e) => setUserForm(prev => ({ ...prev, is_teste: e.target.value === 'teste' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                  >
                    <option value="pago">Pago</option>
                    <option value="teste">Teste</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Permissões</label>
                  <select
                    value={userForm.admin ? 'admin' : 'usuario'}
                    onChange={(e) => setUserForm(prev => ({ ...prev, admin: e.target.value === 'admin' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-admin-500 focus:border-admin-500 transition-all duration-200"
                  >
                    <option value="usuario">Usuário</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUserForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loadingUsers}
                  className="flex-1 px-4 py-2 bg-admin-600 text-white rounded-lg hover:bg-admin-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingUsers ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Salvando...</span>
                    </div>
                  ) : (
                    `${editingUser ? 'Atualizar' : 'Criar'} Usuário`
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
